/**
 * Activity (AIOE 0.65.0): what the organisation's agents did, across every project the reader may see,
 * in plain sentences. AIOE's own contract; the console's Activity page and the phone's Activity screen
 * read it.
 *
 *   GET /activity/v1?from=&to=&projectId=&deviceId=&person=&attention=1&limit=&before=
 *       → ActivityResponse; ETag, 304 on If-None-Match
 *
 * Members read the runs of the projects they belong to and of their own machines; administrators and
 * operators read the organisation's. `items` is newest first: each run (the History row of
 * ./historyRuns.ts with its project, machine and person) and, between them, the notable things that are
 * not runs (an approval asked or answered, an agent's error, a team started or stopped, a task done).
 * `next` pages back through the window, as `before`.
 *
 * Below the contract: the words both clients use, so the console and the phone say the same thing
 * (`activityRunSentence`, `activityRunFacts`, the day headings) and the plain sentence for an event a
 * machine reported (`machineEventSentence`, with repeats folded by `foldMachineEvents`). Audit records
 * travel as events of kind `audit`; they belong to the Audit page and are left out here.
 */
export const ACTIVITY_SCHEMA_VERSION = 1;
/** Items per page: the default and the most one page answers. */
export const ACTIVITY_DEFAULT_LIMIT = 50;
export const ACTIVITY_MAX_LIMIT = 100;
/** The longest window one request may name. */
export const ACTIVITY_MAX_DAYS = 31;
export const ACTIVITY_EVENT_KINDS = ["approval.asked", "approval.answered", "approval.expired", "agent.error", "team.started", "team.stopped", "task.done"];
// ── Words ──────────────────────────────────────────────────────────────
export const LOCALE_AU = "en-AU";
/** "42 min", "1 h 5 min", "35 s". */
export function durationWords(ms) {
    const s = Math.max(0, Math.round(ms / 1000));
    if (s < 60)
        return `${s} s`;
    const m = Math.round(s / 60);
    if (m < 60)
        return `${m} min`;
    const h = Math.floor(m / 60);
    const rest = m % 60;
    return rest ? `${h} h ${rest} min` : `${h} h`;
}
const plural = (n, one, many) => `${n.toLocaleString(LOCALE_AU)} ${n === 1 ? one : many}`;
const quote = (t) => `“${t.length > 90 ? `${t.slice(0, 89).trimEnd()}…` : t}”`;
export const ACTIVITY_STATUS_WORDS = { running: "Running", ok: "Finished", error: "Failed", cancelled: "Cancelled" };
/** "Scout in Feature squad finished “Add the billing page”". */
export function activityRunSentence(run) {
    const team = run.teamId === "solo" ? "" : ` in ${run.teamName ?? run.teamId}`;
    const what = run.title ?? run.task?.title;
    const q = what ? quote(what) : undefined;
    switch (run.status) {
        case "running":
            return `${run.agent}${team} is working${q ? ` on ${q}` : ""}`;
        case "error":
            return `${run.agent}${team} failed${q ? ` on ${q}` : ""}`;
        case "cancelled":
            return `${run.agent}${team} was stopped${q ? ` on ${q}` : ""}`;
        default:
            return `${run.agent}${team} finished ${q ?? "a run"}`;
    }
}
/** The facts after the sentence: project, how long, tools, files, machine. `project: false` leaves the project out. */
export function activityRunFacts(run, now, opts = {}) {
    const start = Date.parse(run.startedAt);
    const end = run.endedAt ? Date.parse(run.endedAt) : now;
    const out = [];
    if (opts.project !== false)
        out.push(run.projectName);
    if (Number.isFinite(start) && Number.isFinite(end))
        out.push(run.status === "running" ? `${durationWords(end - start)} so far` : durationWords(end - start));
    if (run.toolCalls)
        out.push(plural(run.toolCalls, "tool", "tools"));
    if (run.filesChanged)
        out.push(plural(run.filesChanged, "file", "files"));
    if (run.machine)
        out.push(`on ${run.machine.name}`);
    return out;
}
const dayStart = (t) => {
    const d = new Date(t);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
};
/** The day an item falls on, in the reader's time zone: what a timeline groups by. */
export const activityDayKey = (iso) => {
    const d = new Date(iso);
    return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
};
/** "Today", "Yesterday", "Fri 24 Oct", and with the year once it is not this one. */
export function activityDayLabel(iso, now) {
    const t = Date.parse(iso);
    const today = dayStart(now);
    const day = dayStart(t);
    if (day === today)
        return "Today";
    // A day is not always 24 hours (daylight saving): compare the start of yesterday.
    const y = new Date(today);
    y.setDate(y.getDate() - 1);
    if (day === y.getTime())
        return "Yesterday";
    const sameYear = new Date(t).getFullYear() === new Date(now).getFullYear();
    return new Date(t).toLocaleDateString(LOCALE_AU, { weekday: "short", day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) }).replace(/,/g, "");
}
/** "9:40 am". */
export const activityTime = (iso) => new Date(iso).toLocaleTimeString(LOCALE_AU, { hour: "numeric", minute: "2-digit" });
/** The start of a window ending now, in the reader's time zone: today, or this many days counting today. */
export function activityWindowStart(days, now) {
    const d = new Date(dayStart(now));
    d.setDate(d.getDate() - (days - 1));
    return d.toISOString();
}
// ── Approvals ──────────────────────────────────────────────────────────
/** The longest a request's own words run in a sentence. */
const REQUEST_CAP = 60;
/** The kind line a workbench puts in an approval's body ("Command · 2 of 3 from Scout"), as the words a sentence uses. */
const REQUEST_KINDS = { command: "run a command", "file change": "change a file", delete: "delete a file", read: "read a file", "web fetch": "fetch a web page", tool: "use a tool", action: "do something" };
/** Something a person would read as a shell command line. */
function looksLikeCommand(t) {
    return /^(?:sudo\s+)?[a-z0-9_./~-]+(?:\s|$)/.test(t) && (/\s-{1,2}[A-Za-z]|[|&;<>$`]|\s\.{0,2}\//.test(t) || /^(?:git|npm|npx|pnpm|yarn|node|python3?|pip|cargo|go|make|docker|kubectl|az|gh|curl|wget|rm|mv|cp|mkdir|ls|cat|sed|awk|grep|find|chmod|chown|tar|bash|sh|dotnet|terraform)\b/.test(t));
}
/**
 * A request's raw words, short and on one line: the first line only, nothing of a heredoc, at most about sixty
 * characters cut at a word, and "…" where anything was left out. When something was cut, the dangling flags before
 * the cut go too ("git commit -q --amend -F - <<'EOF'…" is "git commit …").
 */
export function shortRequestText(raw, cap = REQUEST_CAP) {
    let t = raw.replace(/\r/g, "");
    let cut = false;
    const nl = t.indexOf("\n");
    if (nl >= 0) {
        cut = cut || t.slice(nl).trim() !== "";
        t = t.slice(0, nl);
    }
    const here = t.search(/<<-?\s*['"]?[A-Za-z_]/);
    if (here >= 0) {
        t = t.slice(0, here);
        cut = true;
    }
    t = t.replace(/\s+/g, " ").trim().replace(/^[`'"]+|[`'"]+$/g, "");
    if (t.length > cap) {
        const head = t.slice(0, cap);
        const sp = head.lastIndexOf(" ");
        t = sp > cap / 3 ? head.slice(0, sp) : head;
        cut = true;
    }
    if (cut) {
        const words = t.split(" ");
        while (words.length > 1 && /^(?:-{1,2}[\w-]*|[|&;<>]+)$/.test(words[words.length - 1]))
            words.pop();
        t = words.join(" ");
    }
    return cut ? `${t} …` : t;
}
/** What an approval asks, from the record a workbench filed: who asks, and the request as short plain words. */
export function approvalAsk(title, body) {
    const kindLine = body?.split("\n").map((l) => l.trim()).find((l) => /^(Command|File change|Delete|Read|Web fetch|Tool|Action)(\s·|$)/.test(l));
    const kind = kindLine ? kindLine.split(" ·")[0].toLowerCase() : undefined;
    let agent;
    let what = title.replace(/^(approve|approval|needs approval)\s*[:–-]\s*/i, "").trim();
    // A held action (heldActionTitle): the agent by name and that a person has to say yes; the list above says what it does.
    const held = /^(.{1,80}?) wants to do something that waits for a person: /.exec(what);
    if (held)
        return { agent: held[1].trim(), ask: "do something that waits for a person's yes" };
    const asks = /^(.{1,80}?) asks to ([\s\S]+)$/.exec(what);
    const gated = /^(.{1,80}?) wants to use ([^:\n]{1,100}): ([\s\S]+)$/.exec(what);
    if (gated) {
        agent = gated[1].trim();
        return { agent, ask: `use ${gated[2].trim()} (${shortRequestText(gated[3], 40)})` };
    }
    if (asks) {
        agent = asks[1].trim();
        what = asks[2];
    }
    const short = shortRequestText(what);
    if (kind === "command" || (!kind && looksLikeCommand(what.trim())))
        return { ...(agent ? { agent } : {}), ask: `run a command (${short})` };
    // A plain description already ("use a tool", "Read src/a.ts"): as it is, lower-cased where it leads the clause.
    const plain = /^[A-Z][a-z]/.test(short) ? short.charAt(0).toLowerCase() + short.slice(1) : short;
    if (kind && REQUEST_KINDS[kind] && !/^(run|change|edit|delete|read|fetch|use|write|create|open)\b/i.test(plain))
        return { ...(agent ? { agent } : {}), ask: `${REQUEST_KINDS[kind]} (${short})` };
    return { ...(agent ? { agent } : {}), ask: plain };
}
/** "Hannah asked to run a command (git commit …) · answered by JP in 2 min": an approval asked and answered as one line. */
export function approvalSentence(a, outcome) {
    const { agent, ask } = approvalAsk(a.title, a.body);
    const head = `${agent ?? (a.teamName ? `An agent in ${a.teamName}` : "An agent")} asked to ${ask}`;
    if (outcome.state === "waiting")
        return head;
    if (outcome.state === "expired")
        return `${head} · nobody answered in time`;
    const after = outcome.afterMs !== undefined ? ` in ${durationWords(outcome.afterMs)}` : "";
    return `${head} · answered${outcome.by ? ` by ${outcome.by}` : outcome.on ? ` on ${outcome.on}` : ""}${after}`;
}
/** Audit records ride the event ring too; the Audit page shows them, never Activity. */
export const isAuditEvent = (kind) => kind === "audit" || kind.startsWith("audit.");
const text = (p, ...keys) => {
    for (const k of keys) {
        const v = p[k];
        if (typeof v === "string" && v.trim())
            return v.trim();
        if (typeof v === "number" && Number.isFinite(v))
            return String(v);
    }
    return undefined;
};
const cut = (t, n = 140) => (t.length > n ? `${t.slice(0, n - 1).trimEnd()}…` : t);
const looksLikeId = (v) => /^[0-9a-f]{8}-[0-9a-f]{4}-|^[A-Za-z0-9_:-]{24,}$/.test(v);
const AGENT_STATUS = {
    thinking: "is thinking",
    tool_calling: "is using a tool",
    idle: "is idle",
    waiting_approval: "is waiting for approval",
    waiting: "is waiting",
    error: "hit an error",
    stopped: "stopped",
    done: "finished",
    starting: "is starting",
    running: "is running",
};
const TOOL_STATUS = { completed: "finished", failed: "failed", in_progress: "is under way", pending: "is waiting", cancelled: "was cancelled" };
const STOP_REASONS = { max_tokens: ", out of room", max_turn_requests: ", at its turn limit", refusal: ", refused", cancelled: ", cancelled" };
const TASK_STATUS = { todo: "to do", "in-progress": "in progress", in_progress: "in progress", review: "in review", done: "done", blocked: "blocked", "not-started": "not started", "wont-do": "won't do" };
/** "Feature squad", or nothing for a team known only by an id. */
function teamWords(p) {
    const name = text(p, "teamName", "name");
    if (name)
        return name;
    const id = text(p, "teamId");
    return id && id !== "solo" && !looksLikeId(id) ? id : undefined;
}
/** One plain sentence for an event a machine reported: never JSON, never an id. */
export function machineEventSentence(kind, payload) {
    const s = eventWords(kind, payload);
    return s.charAt(0).toUpperCase() + s.slice(1);
}
function eventWords(kind, payload) {
    const p = payload && typeof payload === "object" && !Array.isArray(payload) ? payload : {};
    const agent = text(p, "agentName", "memberName") ?? "An agent";
    switch (kind) {
        case "agent.tool_call": {
            const title = text(p, "title", "toolName", "kind");
            return title ? `${agent} used ${cut(title, 100)}` : `${agent} used a tool`;
        }
        case "agent.tool_call_update":
            return `A tool call ${TOOL_STATUS[text(p, "status") ?? ""] ?? "changed"}`;
        case "agent.tool_request":
        case "permission.requested":
        case "permission.request":
            return `${agent} asked for approval${text(p, "title") ? `: ${cut(text(p, "title"), 100)}` : ""}`;
        case "permission.resolved":
            return `A request for approval was ${text(p, "decision") === "deny" ? "refused" : "allowed"}`;
        case "agent.prompt_submitted":
            return `${agent} was sent a message`;
        case "agent.turn_complete":
            return `${agent} finished its turn${STOP_REASONS[text(p, "stopReason") ?? ""] ?? ""}`;
        case "agent.status":
        case "agent.status_change":
        case "session.status": {
            const s = text(p, "status", "state");
            return `${agent} ${s ? (AGENT_STATUS[s] ?? s.replace(/_/g, " ")) : "changed state"}`;
        }
        case "agent.error":
            return `${agent} hit an error${text(p, "message") ? `: ${cut(text(p, "message"))}` : ""}`;
        case "agent.restarted":
            return `${agent} was restarted${text(p, "reason") ? `: ${cut(text(p, "reason"))}` : ""}`;
        case "team.started": {
            const n = typeof p.agents === "number" ? p.agents : undefined;
            return `${teamWords(p) ?? "A team"} started${n ? ` with ${plural(n, "agent", "agents")}` : ""}`;
        }
        case "team.stopped":
            return `${teamWords(p) ?? "A team"} stopped`;
    }
    if (kind.startsWith("task.")) {
        const title = text(p, "title");
        const named = title ? `Task ${quote(title)}` : "A task";
        const what = kind.slice(5);
        const status = text(p, "status");
        if (what === "created")
            return `${named} was added`;
        if (what === "deleted" || what === "removed")
            return `${named} was removed`;
        if (status && (what === "status" || what === "moved" || what === "updated" || what === "changed"))
            return `${named} moved to ${TASK_STATUS[status] ?? status.replace(/[-_]/g, " ")}`;
        return `${named} changed`;
    }
    // Another kind (the ring is an open set): its name in words, and the line most events carry.
    const lead = kind.replace(/[._-]+/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").trim().toLowerCase().replace(/^./, (c) => c.toUpperCase()) || "Something happened";
    const said = text(p, "summary", "message", "title", "text");
    return said && !looksLikeId(said) ? `${lead}: ${cut(said)}` : lead;
}
/** How many of the newest lines a repeat may fold back into. */
const FOLD_LOOKBACK = 4;
/**
 * Plain sentences, newest first, audit records left out, and a repeat (the same sentence from the same machine,
 * again and again with at most a few other lines between) folded into one line with its count and span.
 */
export function foldMachineEvents(events) {
    const sorted = events.filter((e) => !isAuditEvent(e.kind)).sort((a, b) => b.at - a.at || b.seq - a.seq);
    const out = [];
    for (const e of sorted) {
        const sentence = machineEventSentence(e.kind, e.payload);
        // A repeat folds into its line even with a few other lines between (an agent's tool calls and its
        // status changes take turns), as long as it is among the last few lines.
        const same = out.slice(-FOLD_LOOKBACK).find((f) => f.sentence === sentence && f.deviceId === e.deviceId);
        if (same) {
            same.count++;
            same.firstAt = e.at;
            continue;
        }
        out.push({ key: `${e.deviceId ?? ""}/${e.workspaceId ?? ""}/${e.seq}`, sentence, ...(e.deviceId ? { deviceId: e.deviceId } : {}), kind: e.kind, count: 1, at: e.at, firstAt: e.at });
    }
    return out;
}
/** "An agent used Read, 197 times between 1:32 and 1:48 pm" (one time: the sentence alone; within a minute, the count alone). */
export function foldedSentence(f) {
    if (f.count <= 1)
        return f.sentence;
    const a = activityTime(f.firstAt);
    const b = activityTime(f.at);
    // "1:32 pm" and "1:48 pm" share the half of the day: say it once.
    const sameHalf = a.slice(-2) === b.slice(-2);
    // Within one minute the line's own time says when: just the count.
    if (a === b)
        return `${f.sentence}, ${f.count.toLocaleString(LOCALE_AU)} times`;
    return `${f.sentence}, ${f.count.toLocaleString(LOCALE_AU)} times between ${sameHalf ? a.replace(/\s?[ap]m$/i, "") : a} and ${b}`;
}
//# sourceMappingURL=activity.js.map